import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';

/**
 * Custom node for modules/packages in the architecture graph
 */
function ModuleNode({ data, selected }) {
  const { archMode, coupling, searchMatch, cycleHighlight, fanInHighlight, boundaryMode, boundary } = data;
  const isDepsMode = archMode === 'dependencies';

  // Determine color class based on mode
  let couplingClass = '';
  if (boundaryMode && boundary) {
    couplingClass = `boundary-${boundary.layer}`;
  } else if (isDepsMode && coupling?.level) {
    couplingClass = `coupling-${coupling.level}`;
  }

  // Always apply boundary color to left border when available (semantic layer indicator)
  const boundaryStyle = boundary?.color
    ? { borderLeftColor: boundary.color }
    : {};

  // Build class names
  const classNames = [
    'graph-node',
    'module-node',
    selected ? 'selected' : '',
    couplingClass,
    isDepsMode && coupling?.inCycle ? 'in-cycle' : '',
    cycleHighlight ? 'cycle-highlight' : '',
    fanInHighlight ? 'fan-in-highlight' : '',
    searchMatch ? `search-${searchMatch}` : ''
  ].filter(Boolean).join(' ');

  return (
    <div className={classNames} style={boundaryStyle}>
      <Handle type="target" position={Position.Top} />
      <div className="node-icon">
        {isDepsMode && coupling?.inCycle ? (
          // Cycle warning icon
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        ) : (
          // Folder icon
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
          </svg>
        )}
      </div>
      <div className="node-content">
        <div className="node-header">
          <div className="node-label">{data.label}</div>
          {boundary?.label && (
            <span
              className="role-badge"
              style={{ backgroundColor: boundary.color }}
              title={`Inferred role: ${boundary.label}${boundary.source === 'ckb' ? ' (from CKB)' : ' (inferred from path)'}`}
            >
              {boundary.label}
            </span>
          )}
        </div>
        {isDepsMode && coupling ? (
          // Dependencies mode: show coupling metrics
          <div className="node-coupling">
            <span className="coupling-metric" title="Fan-in: modules that depend on this">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
              {coupling.fanIn}
            </span>
            <span className="coupling-metric" title="Fan-out: modules this depends on">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="18 15 12 9 6 15"/>
              </svg>
              {coupling.fanOut}
            </span>
            {coupling.inCycle && (
              <span className="cycle-badge" title="Part of a dependency cycle">cycle</span>
            )}
          </div>
        ) : (
          // Structure mode: show file count
          <div className="node-meta">
            {data.fileCount && <span>{data.fileCount} files</span>}
            {data.symbolCount && <span>{data.symbolCount} symbols</span>}
            {data.language && <span className="node-badge">{data.language}</span>}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
}

export default memo(ModuleNode);
